import React, { useEffect, useState } from "react";
import {
  styled,
  Tooltip,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { colors } from "../../constants/colors";
import { ReactComponent as Additem } from "../../assets/icons/additem.svg";
import OptionsButtonCustom from "../option-button/OptionsButtonCustom";

// Types
interface HeaderColumn {
  key: string;
  label: string;
  width: number; // Grid width (1-12)
  align?: "left" | "center" | "right";
  render?: (value: any, rowData: any, rowIndex: number) => React.ReactNode;
}

interface AccordionTableProps {
  titleName: string;
  subTitleName?: string;
  required?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  enableButtonLeft?: boolean;
  renderButtonLeft?: React.ReactNode;
  onClickButtonLeft?: () => void;
  enableButtonRight?: boolean;
  renderButtonRight?: React.ReactNode;
  onClickButtonRight?: () => void;
  titleButtonRight?: string;
  colorHeader?: string;
  hoverTitle?: string;
  isView?: boolean;
  data: any[];
  headers: HeaderColumn[];
  emptyStateMessage?: string;
  emptyStateIcon?: React.ReactNode;
  enableRowActions?: boolean;
  onViewClick?: (rowData: any, index: number) => void;
  onDeleteClick?: (rowData: any, index: number) => void;
  onEditClick?: (rowData: any, index: number) => void;
  showDivider?: boolean;
}

// Styled Components
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${colors.themeMainColor}`,
  borderRadius: "8px",
  overflow: "hidden",
}));

const AccordionSummary = styled(
  (props: AccordionSummaryProps & { bgColor?: string }) => (
    <MuiAccordionSummary
      expandIcon={
        <ExpandMoreIcon
          sx={{
            fontSize: "1.8rem",
            color: colors.white,
          }}
        />
      }
      {...props}
    />
  )
)(({ theme, bgColor }) => ({
  backgroundColor: bgColor || colors.themeMainColor,
  color: colors.white,
  flexDirection: "row",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginRight: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const TableHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  marginBottom: theme.spacing(1),
}));

const TableRow = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(6, 2),
  color: theme.palette.grey[500],
}));

// Main Component
const AccordionTable: React.FC<AccordionTableProps> = ({
  titleName,
  subTitleName,
  required = false,
  defaultExpanded = false,
  expanded,
  enableButtonLeft = false,
  renderButtonLeft,
  onClickButtonLeft,
  enableButtonRight = false,
  renderButtonRight,
  onClickButtonRight,
  titleButtonRight = "เพิ่มข้อมูล",
  colorHeader,
  hoverTitle,
  isView = false,
  data = [],
  headers = [],
  emptyStateMessage = "ยังไม่มีข้อมูล",
  emptyStateIcon,
  enableRowActions = true,
  onViewClick,
  onDeleteClick,
  onEditClick,
  showDivider = true,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const handleToggle = (): void => {
    if (expanded === undefined) {
      setIsExpanded((prev) => !prev);
    }
  };

  const renderLeftButton = (): React.ReactNode => {
    if (renderButtonLeft) {
      return renderButtonLeft;
    }

    return (
      <Tooltip title={hoverTitle || ""}>
        {/* <IconButton
         
        >
          <Additem />
        </IconButton> */}
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClickButtonLeft?.();
          }}
          variant="contained"
          color="success"
          sx={{ color: colors.white }} startIcon={<Additem />}>
          เพิ่ม
        </Button>
      </Tooltip>
    );
  };

  const renderRightButton = (): React.ReactNode => {
    if (renderButtonRight) {
      return renderButtonRight;
    }

    return (
      <Button
        variant="text"
        startIcon={<Additem />}
        onClick={(e) => {
          e.stopPropagation();
          onClickButtonRight?.();
        }}
        sx={{
          color: colors.white,
          textTransform: "none",
        }}
      >
        {titleButtonRight}
      </Button>
    );
  };

  const renderCellContent = (
    header: HeaderColumn,
    value: any,
    rowData: any,
    rowIndex: number,
    is_amount?: boolean
  ): React.ReactNode => {
    if (header.render) {
      return header.render(value, rowData, rowIndex);
    }

    // Default rendering based on value type
    if (value === null || value === undefined) {
      return "-";
    }

    if (typeof value === "string" || typeof value === "number") {
      return (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {is_amount ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, minimumIntegerDigits: 2 }) : value}
        </Typography>
      );
    }

    return value;
  };

  const getAlignValue = (align?: string) => {
    switch (align) {
      case "center":
        return "center";
      case "right":
        return "right";
      default:
        return "left";
    }
  };

  return (
    <Accordion defaultExpanded={defaultExpanded} expanded={isExpanded}>
      <AccordionSummary
        aria-controls="panel-content"
        id="panel-header"
        bgColor={colorHeader}
        onClick={handleToggle}
      >
        {!isView
          && enableButtonLeft
          && renderLeftButton()}

        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: colors.white,
              marginX: 1,
              fontWeight: 500,
            }}
          >
            {titleName}
            {required && (
              <Typography
                component="span"
                sx={{ color: "error.main", ml: 0.5 }}
              >
                *
              </Typography>
            )}
          </Typography>

          {subTitleName && (
            <Typography
              variant="body2"
              sx={{
                color: colors.white,
                opacity: 0.8,
                ml: 1,
              }}
            >
              {subTitleName}
            </Typography>
          )}
        </Box>

        {enableButtonRight && (
          <Box sx={{ ml: "auto", mr: 2 }}>{renderRightButton()}</Box>
        )}
      </AccordionSummary>

      <AccordionDetails>
        <Box>
          {/* Dynamic Header */}
          {headers.length > 0 && (
            <TableHeader elevation={0}>
              <Grid container spacing={2} alignItems="center">
                {headers.map((header) => (
                  <Grid item xs={header.width} key={header.key}>
                    <Typography
                      variant="subtitle2"
                      align={getAlignValue(header.align)}
                    >
                      {header.label}
                    </Typography>
                  </Grid>
                ))}
                {enableRowActions && !isView && (
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" align="center">
                      จัดการ
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </TableHeader>
          )}

          {/* Data Rows */}
          {data.length === 0 ? (
            <EmptyState>
              {emptyStateIcon || (
                <FileIcon sx={{ fontSize: 48, color: "grey.300", mb: 2 }} />
              )}
              <Typography variant="body1" gutterBottom>
                {emptyStateMessage}
              </Typography>
            </EmptyState>
          ) : (
            data.map((rowData, rowIndex) => (
              <React.Fragment key={rowData.id || rowIndex}>
                <Grid container spacing={2} alignItems="center">
                  {headers.map((header) => {
                    const is_amount = ['spending_info_value'].includes(header?.key)
                    return (
                      <Grid item xs={header.width} key={header.key}>
                        <Box
                          sx={{
                            textAlign: getAlignValue(header.align),
                          }}
                        >
                          {renderCellContent(
                            header,
                            rowData[header.key],
                            rowData,
                            rowIndex,
                            is_amount
                          )}
                        </Box>
                      </Grid>
                    )
                  })}

                  {/* Row Actions */}
                  {enableRowActions && !isView && (
                    <Grid
                      item
                      xs={2}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <OptionsButtonCustom
                        enableView={!!onViewClick}
                        enableDelete={!!onDeleteClick}
                        enableEdit={!!onEditClick}
                        onViewClick={() => onViewClick?.(rowData, rowIndex)}
                        onDeleteClick={() => onDeleteClick?.(rowData, rowIndex)}
                        onEditClick={() => onEditClick?.(rowData, rowIndex)}
                      />
                    </Grid>
                  )}
                </Grid>

                {/* Divider */}
                {showDivider && rowIndex < data.length - 1 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        borderTop: "1px dashed #DDE1EB",
                        mt: 1,
                        mb: 1,
                      }}
                    />
                  </Grid>
                )}
              </React.Fragment>
            ))
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionTable;
